// ScalingData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string;
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    // treatmentAreas1: TreatmentArea[];
    // treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const scaling: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "teeth-cleaning-dubai",
        "meta": {
            "title": "Teeth Cleaning Dubai | Professional Dental Cleaning Services",
            "description": "Professional teeth cleaning service in Dubai to remove plaque, tartar, and stains. Promotes healthy gums, fresh breath, and a brighter smile with expert dental care.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/teeth-cleaning-dubai/",
            "og": {
                "title": "Teeth Cleaning Dubai | Professional Dental Cleaning Services",
                "description": "Professional teeth cleaning and polishing for a healthier, brighter smile",
                "image": "https://www.elitebodyhome.com/images/teeth-cleaning-og.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Teeth Cleaning Dubai"],
        "hero": {
            "title": "Professional Teeth Cleaning & Polishing",
            "subtitle": "For a Healthier, Brighter Smile",
            "description": "At our Dubai dental clinic, we use state-of-the-art technology to offer you a complete cleaning experience. Whether you require scaling and polishing or periodic maintenance, our highly qualified dental specialists guarantee that these absolutely free bite holders remain in top shape."
        },
        "howItWorks": {
            "title": "The Importance of Professional Teeth Cleaning",
            "description": "Regular professional cleaning is a must for oral health. Let us discuss the main advantages of professional dental cleaning here:"
        },
        "procedureSteps": {
            "heading": "How We Clean Your Teeth: Scaling, Polishing, and More",
            "para": "At Elite Body Home Polyclinic, we have a comprehensive and gentle teeth cleaning procedure that consists of:",
            "steps": [
                {
                    "title": "Consultation",
                    "desc": "It all begins with a custom-tailored consultation where we evaluate your oral health and create a personalized treatment plan.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "Scaling",
                    "desc": "We use sophisticated equipment to removes plaque and tartar deposits.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "Polishing",
                    "desc": "Next comes polish, an important step in cleaning your teeth.",
                    "icon": "fa-solid fa-sparkles"
                },
                {
                    "title": "Fluoride Treatment",
                    "desc": "So, it's no surprise that when you visit the dentist, they often give you a fluoride treatment, which protects your teeth against decay by strengthening your enamel.",
                    "icon": "fa-solid fa-shield-alt"
                },
                {
                    "title": "Aftercare Advice",
                    "desc": "Our team is here to share useful tips about at-home dental hygiene to keep your smile shining.",
                    "icon": "fa-solid fa-lightbulb"
                }
            ]
        },
        "benefits": {
            "heading": "Teeth Cleaning Offers",
            "subheading": "",
            "para": "Professional teeth cleaning provides numerous benefits for your oral health and overall wellbeing:",
            "items": [
                {
                    "title": "Healthy Gums & Cavity Prevention",
                    "desc": "Keeps gums healthy and prevents cavities",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "Whiter Teeth",
                    "desc": "Whitens teeth by removing surface stains for a brighter smile",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "Improved Oral Health",
                    "desc": "Improves overall oral health",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "Fresh Breath",
                    "desc": "Freshens your breath",
                    "icon": "fa-solid fa-wind"
                },
                {
                    "title": "Early Detection",
                    "desc": "Detects dental issues early",
                    "icon": "fa-solid fa-magnifying-glass"
                },
                {
                    "title": "Overall Health",
                    "desc": "Fastens to deadly health threats",
                    "icon": "fa-solid fa-shield-heart"
                }
            ]
        },
        "eligibility": {
            "heading": "Who Needs Teeth Cleaning?",
            "subheading": "",
            "para": "Professional teeth cleaning is recommended for everyone, but especially important for:",
            "items": [
                {
                    "title": "Adults & Children",
                    "desc": "Everyone over age 3 should have regular dental cleanings",
                    "icon": "fa-solid fa-people-group"
                },
                {
                    "title": "Tobacco Users",
                    "desc": "Smokers and tobacco users need more frequent cleanings",
                    "icon": "fa-solid fa-smoking"
                },
                {
                    "title": "Coffee/Tea Drinkers",
                    "desc": "Those who consume staining beverages regularly",
                    "icon": "fa-solid fa-mug-hot"
                },
                {
                    "title": "People with Dental Work",
                    "desc": "Those with crowns, bridges or implants need special cleaning",
                    "icon": "fa-solid fa-tooth"
                }
            ]
        },
        "preparationTips": {
            "heading": "Precautions Post Dental Cleaning",
            "subheading": "",
            "para": "After your professional teeth cleaning, follow these guidelines for optimal results:",
            "items": [
                {
                    "title": "Avoid Eating/Drinking",
                    "desc": "Avoid eating or drinking right away (if you use fluoride)",
                    "icon": "fa-solid fa-utensils-slash"
                },
                {
                    "title": "Soft Foods",
                    "desc": "Stick to soft foods immediately after cleaning",
                    "icon": "fa-solid fa-apple-whole"
                },
                {
                    "title": "Avoid Staining Beverages",
                    "desc": "Avoid coffee, tea, wine and other staining drinks for 24 hours",
                    "icon": "fa-solid fa-wine-glass-empty"
                },
                {
                    "title": "Gentle Brushing",
                    "desc": "Be gentle when brushing and flossing after cleaning",
                    "icon": "fa-solid fa-teeth-open"
                },
                {
                    "title": "Stay Hydrated",
                    "desc": "Drink plenty of water to help oral health",
                    "icon": "fa-solid fa-droplet"
                }
            ]
        },
        "results": {
            "heading": "What to Expect After Cleaning",
            "subheading": "",
            "para": "After your professional teeth cleaning, you can expect:",
            "items": [
                {
                    "title": "Cleaner Teeth",
                    "desc": "Your teeth will feel incredibly clean and smooth",
                    "icon": "fa-solid fa-sparkles"
                },
                {
                    "title": "Brighter Smile",
                    "desc": "Noticeable improvement in tooth color and brightness",
                    "icon": "fa-solid fa-face-grin-stars"
                },
                {
                    "title": "Healthier Gums",
                    "desc": "Reduced gum inflammation and bleeding",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "Fresh Breath",
                    "desc": "Elimination of bad breath caused by plaque buildup",
                    "icon": "fa-solid fa-wind"
                }
            ]
        },
        "aftercareTips": {
            "heading": "What Makes Our Dental Cleaning Different?",
            "subheading": "",
            "para": "Elite Body Home Polyclinic offers the best teeth cleaning services in Dubai. We are a team of experienced our field and are trained to perform dental cleanings, in a gentle, yet individualized method. We provide complete scaling and polishing using latest technology to remove plaque and tartar from your teeth ensuring a cleaner, brighter and healthier smile. If you just want a routine cleaning or need extensive dental care we have affordable options available without sacrificing quality. As the most popular dental clinic providing teeth cleaning near me, we offer a convenient solution. That is a minimum for us, you can take our word for it, we will ensure it being an outstanding dental cleaning experience for you.",
            "items": []
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How often should I get my teeth professionally cleaned?",
                    "answer": "Most dentists recommend professional teeth cleaning every 6 months, but some people may need more frequent cleanings depending on their oral health."
                },
                {
                    "question": "Does teeth cleaning hurt?",
                    "answer": "Teeth cleaning is generally not painful, though you may experience some discomfort if you have sensitive teeth or gums. Our dentists use gentle techniques to minimize any discomfort."
                },
                {
                    "question": "How long does a teeth cleaning appointment take?",
                    "answer": "A standard teeth cleaning appointment typically takes between 30-60 minutes, depending on the condition of your teeth and gums."
                },
                {
                    "question": "Can teeth cleaning whiten my teeth?",
                    "answer": "While teeth cleaning removes surface stains and can make your teeth appear whiter, it's not the same as professional teeth whitening treatments which use bleaching agents for more dramatic results."
                }
            ]
        }
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/New-Service-Btuyuanner-Image-scaled.jpg",
            "beforeAfter": []
        },
        "product": {
            "id": 17,
            "title": "Teeth Cleaning Dubai",
            "description": "Professional teeth cleaning and polishing for a healthier, brighter smile",
            "price": 300
        }
    },
    "ar": {
        "url": "teeth-cleaning-dubai",
        "meta": {
            "title": "تنظيف الأسنان في دبي | خدمات تنظيف الأسنان الاحترافية",
            "description": "خدمة تنظيف الأسنان الاحترافية في دبي لإزالة البلاك والجير والتصبغات. تعزيز صحة اللثة، انتعاش النفس، وابتسامة أكثر بياضًا مع رعاية أسنان متخصصة.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/ar/services/teeth-cleaning-dubai",
            "og": {
                "title": "تنظيف الأسنان في دبي | خدمات تنظيف الأسنان الاحترافية",
                "description": "تنظيف وتلميع الأسنان الاحترافي لابتسامة أكثر صحة وإشراقًا",
                "image": "https://www.elitebodyhome.com/images/teeth-cleaning-og-ar.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "تنظيف الأسنان في دبي"],
        "hero": {
            "title": "خدمات تنظيف وتلميع الأسنان الاحترافية",
            "subtitle": "لابتسامة أكثر صحة وإشراقًا",
            "description": "نقدم لك في عيادتنا في دبي تجربة متكاملة لتنظيف وتلميع الأسنان باستخدام أحدث التقنيات. سواء كنت بحاجة إلى إزالة الترسبات وتلميع الأسنان أو إلى جلسات عناية دورية، يضمن لك أطباؤنا المتخصصون الحفاظ على صحة فمك وأسنانك في أفضل حالة ممكنة."
        },
        "howItWorks": {
            "title": "أهمية جلسة تنظيف الأسنان",
            "description": "يُعد التنظيف الاحترافي المنتظم للأسنان خطوة أساسية للحفاظ على صحة الفم. دعنا نستعرض معًا أبرز فوائد تنظيف الأسنان:"
        },
        "procedureSteps": {
            "heading": "كيف نقوم بتنظيف أسنانك؟",
            "para": "يشمل تنظيف الأسنان لدينا إزالة الجير، والتلميع الأسنان، وخدمات أكثر، حيث نعتمد على إجراء تنظيف شامل ولطيف للأسنان، يتضمن الخطوات التالية:",
            "steps": [
                {
                    "title": "استشارة الطبيب",
                    "desc": "تبدأ الجلسة باستشارة مخصصة نقوم خلالها بتقييم صحة فمك ووضع خطة علاجية شخصية تناسب احتياجاتك.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "إزالة الجير",
                    "desc": "نستخدم أجهزة متطورة لإزالة طبقات البلاك والجير المتراكمة.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "تلميع الأسنان",
                    "desc": "نقوم بتلميع الأسنان بعد إزالة الجير، وهي خطوة هامة لتنظيف الأسنان.",
                    "icon": "fa-solid fa-sparkles"
                },
                {
                    "title": "علاج الفلورايد",
                    "desc": "لا تندهش من استخدام الطبيب لعلاج الفلورايد أثناء زيارتك، حيث يعمل هذا العلاج على حماية الأسنان من التسوس من خلال تقوية مينا الأسنان.",
                    "icon": "fa-solid fa-shield-alt"
                },
                {
                    "title": "نصائح العناية بعد العلاج",
                    "desc": "يقدم لك فريقنا المتخصص إرشادات مفيدة للعناية المنزلية بالأسنان للحفاظ على إشراقة ابتسامتك.",
                    "icon": "fa-solid fa-lightbulb"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد تنظيف الأسنان",
            "subheading": "",
            "para": "يوفر تنظيف الأسنان الاحترافي العديد من الفوائد لصحة فمك ورفاهيتك العامة:",
            "items": [
                {
                    "title": "صحة اللثة والوقاية من التسوس",
                    "desc": "يحافظ على صحة اللثة ويمنع تسوس الأسنان.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "أسنان أكثر بياضًا",
                    "desc": "يزيل التصبغات السطحية ويعالج اصفرار الأسنان ليمنحك ابتسامة أكثر بياضًا.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "تحسين صحة الفم",
                    "desc": "يعزز الصحة العامة للفم.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "انتعاش النفس",
                    "desc": "ينعش رائحة النفس.",
                    "icon": "fa-solid fa-wind"
                },
                {
                    "title": "الكشف المبكر",
                    "desc": "يساعد في الاكتشاف المبكر لمشكلات الأسنان.",
                    "icon": "fa-solid fa-magnifying-glass"
                },
                {
                    "title": "الصحة العامة",
                    "desc": "يمنع حدوث مضاعفات صحية خطيرة مرتبطة بصحة الفم.",
                    "icon": "fa-solid fa-shield-heart"
                }
            ]
        },
        "eligibility": {
            "heading": "من يحتاج إلى تنظيف الأسنان؟",
            "subheading": "",
            "para": "يُنصح الجميع بتنظيف الأسنان الاحترافي، لكنه مهم بشكل خاص ل:",
            "items": [
                {
                    "title": "البالغون والأطفال",
                    "desc": "يجب أن يحصل كل شخص فوق سن 3 سنوات على تنظيف أسنان منتظم.",
                    "icon": "fa-solid fa-people-group"
                },
                {
                    "title": "مدخنو التبغ",
                    "desc": "يحتاج المدخنون ومتعاطو التبغ إلى تنظيف أكثر تكرارًا.",
                    "icon": "fa-solid fa-smoking"
                },
                {
                    "title": "شاربو القهوة/الشاي",
                    "desc": "أولئك الذين يستهلكون المشروبات المسببة للتصبغات بانتظام.",
                    "icon": "fa-solid fa-mug-hot"
                },
                {
                    "title": "أصحاب التركيبات السنية",
                    "desc": "أولئك الذين لديهم تيجان أو جسور أو غرسات يحتاجون إلى تنظيف خاص.",
                    "icon": "fa-solid fa-tooth"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاحتياطات بعد جلسة تنظيف الأسنان",
            "subheading": "",
            "para": "بعد جلسة تنظيف الأسنان الاحترافية، اتبع هذه الإرشادات للحصول على أفضل النتائج:",
            "items": [
                {
                    "title": "تجنب الأكل/الشرب",
                    "desc": "تجنب الأكل أو الشرب بعد الجلسة مباشرةً، خاصةً إذا تم استخدام الفلورايد.",
                    "icon": "fa-solid fa-utensils-slash"
                },
                {
                    "title": "الأطعمة اللينة",
                    "desc": "الالتزام بالأطعمة اللينة مباشرة بعد التنظيف.",
                    "icon": "fa-solid fa-apple-whole"
                },
                {
                    "title": "تجنب المشروبات المسببة للتصبغات",
                    "desc": "تجنب القهوة والشاي والنبيذ والمشروبات الأخرى المسببة للتصبغات لمدة 24 ساعة.",
                    "icon": "fa-solid fa-wine-glass-empty"
                },
                {
                    "title": "تنظيف لطيف",
                    "desc": "استخدم فرشاة الأسنان والخيط بلطف بعد التنظيف.",
                    "icon": "fa-solid fa-teeth-open"
                },
                {
                    "title": "حافظ على رطوبة فمك",
                    "desc": "اشرب الكثير من الماء للمساعدة في الحفاظ على صحة الفم.",
                    "icon": "fa-solid fa-droplet"
                }
            ]
        },
        "results": {
            "heading": "ما يمكن توقعه بعد التنظيف",
            "subheading": "",
            "para": "بعد جلسة تنظيف الأسنان الاحترافية، يمكنك توقع:",
            "items": [
                {
                    "title": "أسنان أنظف",
                    "desc": "ستشعر بأن أسنانك نظيفة وناعمة بشكل لا يصدق.",
                    "icon": "fa-solid fa-sparkles"
                },
                {
                    "title": "ابتسامة أكثر إشراقًا",
                    "desc": "تحسن ملحوظ في لون الأسنان وبياضها.",
                    "icon": "fa-solid fa-face-grin-stars"
                },
                {
                    "title": "لثة أكثر صحة",
                    "desc": "تقليل التهاب اللثة والنزيف.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "نفس منعش",
                    "desc": "القضاء على رائحة الفم الكريهة الناتجة عن تراكم البلاك.",
                    "icon": "fa-solid fa-wind"
                }
            ]
        },
        "aftercareTips": {
            "heading": "ما الذي يميز تنظيف الأسنان في عيادات ايليت بودي هوم؟",
            "subheading": "",
            "para": "نقدم في ايليت بودي هوم أفضل خدمات تنظيف الأسنان في دبي. نحن فريق من أطباء الأسنان المتمرسين والمدربين على تقديم جلسات تنظيف أسنان بطريقة لطيفة ومخصصة لكل مريض، نستخدم أحدث التقنيات في تنظيف وتلميع الأسنان، لإزالة طبقات الجير والبلاك بشكل كامل، مما يمنحك ابتسامة نظيفة، وأكثر بياضًا، وصحة فم مثالية. سواء كنت تبحث عن جلسة تنظيف دورية أو تحتاج إلى رعاية للفم متقدمة، نوفر لك خيارات علاجية بأسعار مناسبة دون التضحية بالجودة، حيث نعد من أكثر العيادات شهرة في دبي في مجال تنظيف الأسنان، نقدم لك حلاً مريحًا ومميزًا بالقرب منك. نعدك بأن تجربتك معنا لن تكون فقط مُرضية، بل ستكون استثنائية بكل المقاييس.",
            "items": []
        },
        "faqData": {
            "heading": "الأسئلة المتكررة",
            "subheading": "",
            "items": [
                {
                    "question": "كم مرة يجب أن أخضع لتنظيف الأسنان الاحترافي؟",
                    "answer": "ينصح معظم أطباء الأسنان بالحصول على تنظيف احترافي كل 6 أشهر، ولكن قد يحتاج بعض الأشخاص إلى تنظيف أكثر تكرارًا حسب حالة صحة فمهم."
                },
                {
                    "question": "هل تنظيف الأسنان مؤلم؟",
                    "answer": "تنظيف الأسنان بشكل عام غير مؤلم، على الرغم من أنك قد تشعر ببعض الانزعاج إذا كانت أسنانك أو لثتك حساسة. يستخدم أطباؤنا تقنيات لطيفة لتقليل أي انزعاج."
                },
                {
                    "question": "كم تستغرق جلسة تنظيف الأسنان؟",
                    "answer": "تستغرق جلسة تنظيف الأسنان القياسية عادةً ما بين 30-60 دقيقة، اعتمادًا على حالة أسنانك ولثتك."
                },
                {
                    "question": "هل يمكن لتنظيف الأسنان تبييض أسناني؟",
                    "answer": "بينما يزيل تنظيف الأسنان البقع السطحية ويمكن أن يجعل أسنانك تبدو أكثر بياضًا، إلا أنه ليس مثل علاجات تبييض الأسنان الاحترافية التي تستخدم مواد التبييض لنتائج أكثر دراماتيكية."
                }
            ]
        }
    }
}